import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddLinkRoundedIcon from "@mui/icons-material/AddLinkRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import Collapse from "@mui/material/Collapse";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { brand, notice as tones } from "../../../theme/businessFlowsTheme";
import { Caution, EmptyState, Notice, Refusal, Surface, Toast, focusRing, quietDanger } from "../../workspaces/WorkspaceUi";
import { canPlatform } from "../../../utils/permissions";
import {
  archiveDatabaseServer,
  archiveServerDatabase,
  fixDatabase,
  getAllOrganisations,
  getDatabaseAffected,
  getDatabaseHealth,
  getDatabaseServers,
  makeDatabaseReady,
  restoreServerDatabase,
  setDatabaseLabel,
} from "../../../actions";
import colourForType from "../../../utils/typeColours";
import AddDatabaseDialog from "./AddDatabaseDialog";
import ServerDialog from "./ServerDialog";
import ServerList, { matchesTerm } from "./ServerList";

// What each verdict means to someone using the app, and how it looks.
const VERDICTS = {
  ready: { label: "Ready", tone: tones.good },
  readyWithWarnings: { label: "Ready, with notes", tone: tones.wait },
  needsAttention: { label: "Needs attention", tone: tones.fix },
  empty: { label: "Empty", tone: tones.quiet },
  unreachable: { label: "Offline", tone: tones.quiet },
};

const VerdictChip = ({ verdict }) => {
  if (!verdict) return <Skeleton variant="rounded" width={96} height={22} />;
  const look = VERDICTS[verdict] || VERDICTS.unreachable;
  return (
    <Chip
      size="small"
      label={look.label}
      sx={{ height: 22, fontWeight: 650, fontSize: "0.72rem", color: look.tone.fg, bgcolor: look.tone.bg, border: `1px solid ${look.tone.line}`, borderRadius: 1.5 }}
    />
  );
};

// The counts at the top are also the filters: one click narrows the list to
// them, another clears it. A real button, so it takes focus and answers Enter
// and Space, and says whether it is on.
const CountChip = ({ id, label, tone, active, onToggle }) => (
  <Chip
    size="small"
    component="button"
    type="button"
    clickable
    aria-pressed={active}
    label={label}
    onClick={() => onToggle(id)}
    sx={{
      fontWeight: 650,
      cursor: "pointer",
      color: active ? "#fff" : tone.fg,
      bgcolor: active ? brand.ink : tone.bg,
      border: `1px solid ${active ? brand.ink : "transparent"}`,
      "&:hover": { bgcolor: active ? brand.ink : tone.bg },
    }}
  />
);

// Which of the three counts a database falls under. One that hasn't been
// checked yet is in none of them, so a filter never claims it.
const VERDICT_GROUP = {
  ready: "ready",
  readyWithWarnings: "ready",
  needsAttention: "attention",
  empty: "other",
  unreachable: "other",
};

const CheckIcon = ({ check }) => {
  if (check.ok) return <CheckCircleRoundedIcon sx={{ color: tones.good.fg, fontSize: 20 }} />;
  if (check.severity === "warning") return <WarningAmberRoundedIcon sx={{ color: tones.wait.fg, fontSize: 20 }} />;
  return <ErrorRoundedIcon sx={{ color: tones.fix.fg, fontSize: 20 }} />;
};

const count = (n) => (typeof n === "number" ? n.toLocaleString() : "–");

// "18 September 2026" - the day is enough for something that was tidied away.
const on = (value) => {
  const when = value ? new Date(value) : null;
  return when && !isNaN(when.getTime()) ? when.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : null;
};

// Archiving takes a database out of Business Flows and leaves every byte of it
// on the Neo4j server. Everything the person needs to know before confirming
// is in the dialog, including the workspaces that would lose it.
function ArchiveDatabaseDialog({ database, busy, detach, error, onDetach, onClose, onConfirm }) {
  const workspaces = database?.workspaces || [];
  const shownAs = database ? database.label || database.key || database.databaseName : "";
  const blocked = workspaces.length > 0 && !detach;
  return (
    <Dialog open={Boolean(database)} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ className: "bfx", sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Archive {shownAs}?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Business Flows stops offering this database: it leaves the switcher, the workspace pickers, the API and the readiness checks.
          Nothing in Neo4j is changed or deleted — the database and everything in it stay exactly as they are — and you can restore it
          here at any time.
        </Typography>
        {database && !database.registered && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            It has never been connected, so nobody loses access — it simply stops being offered here and under Connect a database.
          </Typography>
        )}
        {workspaces.length > 0 && (
          <>
            <Caution sx={{ mt: 2 }} next="The links are archived, not deleted. Restoring the database does not add them back — add it to a workspace again.">
              {workspaces.length === 1 ? `${workspaces[0].name} still uses it` : `${workspaces.map((w) => w.name).join(", ")} still use it`}, and{" "}
              {workspaces.length === 1 ? "its people" : "their people"} lose access at once.
            </Caution>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Checkbox checked={detach} onChange={(e) => onDetach(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  Also remove it from {workspaces.length === 1 ? workspaces[0].name : `${workspaces.length} workspaces`}
                </Typography>
              }
            />
          </>
        )}
        {error && <Refusal sx={{ mt: 2 }}>{error}</Refusal>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={busy || blocked}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
        >
          Archive database
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Where a database lived: cloud (an AuraDB host or an encrypted neo4j+s://
// address) or on-premise (everything else, the main server included). It comes
// from the server's address - backend services/databaseServers.js
// `serverKind` - never from its name, and anything unclassified reads as
// on-premise so no row is ever hidden by it.
const KINDS = { cloud: "Cloud", onPremise: "On-premise" };
const kindOf = (server) => (server?.kind === "cloud" ? "cloud" : "onPremise");

// Everything that has been archived, on every server - behind the "Archived"
// button at the top of the page rather than as a section to scroll past. One
// list, grouped by the server each database was on, with a filter for the two
// kinds. A refusal (the server gone, the name taken since) is shown here,
// beside the database it is about, so the next step is where the person is
// looking.
function ArchivedDatabasesDialog({ open, databases, mayManage, busy, error, onRestore, onClose }) {
  const [filter, setFilter] = useState("all");
  const counts = {
    all: databases.length,
    cloud: databases.filter((database) => kindOf(database) === "cloud").length,
    onPremise: databases.filter((database) => kindOf(database) === "onPremise").length,
  };
  const shown = filter === "all" ? databases : databases.filter((database) => kindOf(database) === filter);

  const groups = [];
  shown.forEach((database) => {
    const heading = database.serverName || "Server no longer connected";
    const group = groups.find((one) => one.heading === heading);
    if (group) group.rows.push(database);
    else groups.push({ heading, rows: [database] });
  });

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ className: "bfx", sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Archived databases</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Out of Business Flows, untouched in Neo4j. Restoring makes the database available again, in no workspace: add it to one to give
          people access.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          {[
            ["all", "All"],
            ["cloud", KINDS.cloud],
            ["onPremise", KINDS.onPremise],
          ].map(([key, label]) => (
            <Chip
              key={key}
              size="small"
              label={`${label} (${counts[key]})`}
              onClick={() => setFilter(key)}
              variant={filter === key ? "filled" : "outlined"}
              sx={{ fontWeight: 650, ...(filter === key ? { bgcolor: brand.ink, color: "#fff" } : null) }}
            />
          ))}
        </Stack>
        {error && <Refusal sx={{ mt: 2 }}>{error}</Refusal>}
        {groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Nothing archived here.
          </Typography>
        ) : (
          groups.map((group) => (
            <Box key={group.heading} sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                {group.heading}
              </Typography>
              <Box sx={{ mt: 0.5, borderRadius: 3, border: `1px solid ${brand.line}` }}>
                {group.rows.map((database, index) => (
                  <Stack
                    key={database.name}
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.25}
                    alignItems={{ sm: "center" }}
                    sx={{ px: 2, py: 1.5, borderTop: index ? `1px solid ${brand.hairline}` : 0 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 650, overflowWrap: "anywhere" }}>
                          {database.label || database.name}
                        </Typography>
                        <Chip size="small" label={KINDS[kindOf(database)]} sx={{ height: 20, fontSize: "0.68rem", fontWeight: 650 }} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }}>
                        {[
                          database.label ? database.name : null,
                          database.archivedBy ? `archived by ${database.archivedBy}` : "archived",
                          on(database.archivedAt),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                    {mayManage && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={busy === `restore-${database.name}` ? <CircularProgress size={14} color="inherit" /> : <UnarchiveOutlinedIcon />}
                        disabled={Boolean(busy)}
                        onClick={() => onRestore(database)}
                        sx={{ alignSelf: { xs: "flex-start", sm: "center" }, flexShrink: 0 }}
                      >
                        Restore
                      </Button>
                    )}
                  </Stack>
                ))}
              </Box>
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={Boolean(busy)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// The objects behind one check, so an admin sees exactly what a fix would
// touch - and can spot what is not really content - before running it.
function AffectedList({ database, check }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    let active = true;
    getDatabaseAffected(database, check.key).then((res) => {
      if (active) setState(res?.success ? res.content : { error: res?.msg || "Couldn't list them." });
    });
    return () => {
      active = false;
    };
  }, [database, check.key]);

  if (!state) {
    return (
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} height={40} />
        ))}
      </Box>
    );
  }
  if (state.error) return <Refusal sx={{ m: 1.5 }}>{state.error}</Refusal>;

  // Two objects with one name, or one linked to nothing, are worth a second
  // look before they become openable.
  const nameCounts = state.items.reduce((acc, item) => ({ ...acc, [item.name || ""]: (acc[item.name || ""] || 0) + 1 }), {});
  const orphans = state.items.filter((item) => item.links === 0).length;
  const duplicates = state.items.filter((item) => item.name && nameCounts[item.name] > 1).length;

  return (
    <Box sx={{ px: 1.5, pb: 1.5 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: "auto", alignSelf: "center" }}>
          {state.items.length < (check.count || 0) ? `Showing ${state.items.length} of ${check.count}` : `${state.items.length} objects`}
        </Typography>
        {orphans > 0 && (
          <Chip size="small" icon={<LinkOffRoundedIcon />} label={`${orphans} linked to nothing`} sx={{ height: 22, fontWeight: 600, fontSize: "0.7rem" }} />
        )}
        {duplicates > 0 && (
          <Chip size="small" icon={<ContentCopyOutlinedIcon />} label={`${duplicates} share a name`} sx={{ height: 22, fontWeight: 600, fontSize: "0.7rem" }} />
        )}
      </Stack>
      {orphans === state.items.length && state.items.length > 0 && (
        <Caution sx={{ mb: 1 }} next="Giving them ids makes them openable from search. Nothing is deleted either way.">
          None of these is linked to anything, so they may be left over from an import rather than content.
        </Caution>
      )}
      <Box sx={{ maxHeight: 300, overflowY: "auto", borderRadius: 2.5, border: `1px solid ${brand.line}` }}>
        {state.items.map((item, index) => (
          <Stack
            key={item.ref || index}
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ px: 1.5, py: 1, borderTop: index ? `1px solid ${brand.line}` : 0, bgcolor: "#fff" }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: colourForType(item.type || ""), flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                {item.name || <Box component="span" sx={{ color: "text.disabled", fontStyle: "italic" }}>No name</Box>}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.type}
                {item.labels.length ? ` · label ${item.labels.join(", ")}` : " · no label"}
                {` · ${item.attributes} ${item.attributes === 1 ? "attribute" : "attributes"}`}
              </Typography>
            </Box>
            {item.links === 0 ? (
              <Tooltip title="Not linked to any other object">
                <LinkOffRoundedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              </Tooltip>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {item.links} {item.links === 1 ? "link" : "links"}
              </Typography>
            )}
          </Stack>
        ))}
      </Box>
    </Box>
  );
}

// Every Neo4j server and the databases on it: is each ready for search, the
// explorer, diagrams and trial users, what would make it so - and connecting
// servers and databases.
export default function DatabasesPage({ user = {} }) {
  const mayFix = canPlatform(user, "settings.manage");
  const mayLabel = canPlatform(user, "tenants.manage");
  const mayManageServers = canPlatform(user, "settings.manage");
  const [servers, setServers] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [labels, setLabels] = useState(user.databaseLabels || {});
  const [health, setHealth] = useState({});
  const [selected, setSelected] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  // `notice` names the theme's palette, so the toast's own text is `toast`.
  const [toast, setToast] = useState("");
  const [labelDraft, setLabelDraft] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [archived, setArchived] = useState([]);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [verdictFilter, setVerdictFilter] = useState("");
  const [restoreError, setRestoreError] = useState("");
  const [archivingDatabase, setArchivingDatabase] = useState(null);
  const [archiveError, setArchiveError] = useState("");
  const [detach, setDetach] = useState(false);
  const [serverDialog, setServerDialog] = useState({ open: false, server: null });
  const [databaseDialog, setDatabaseDialog] = useState({ open: false, mode: "connect", server: null, databaseName: null });
  const [archiving, setArchiving] = useState(null);

  const check = useCallback(async (name) => {
    setHealth((current) => ({ ...current, [name]: undefined }));
    const res = await getDatabaseHealth(name);
    setHealth((current) => ({
      ...current,
      [name]: res?.success ? res.content : { database: name, verdict: "unreachable", checks: [{ key: "error", ok: false, severity: "blocker", title: "Could not check", detail: res?.msg || "" }] },
    }));
  }, []);

  const loadServers = useCallback(async (select) => {
    const res = await getDatabaseServers({ archived: true });
    if (!res?.success) {
      setError(res?.msg || "Couldn't list the database servers.");
      setServers([]);
      return;
    }
    setError("");
    setServers(res.content);
    setArchived(res.archived || []);
    setLabels((all) => {
      const next = { ...all };
      res.content.forEach((server) => server.databases.forEach((db) => db.key && db.label && (next[db.key] = db.label)));
      return next;
    });
    if (select) setSelected(select);
  }, []);

  useEffect(() => {
    loadServers();
    getAllOrganisations().then((res) =>
      setWorkspaces(
        (res?.content || [])
          .filter((org) => !org.archivedAt)
          .map((org) => ({ id: org.id, name: org.name }))
          .sort((a, b) => a.name.localeCompare(b.name))
      )
    );
  }, [loadServers]);

  // Every database the app can open, with the server it lives on.
  const entries = useMemo(
    () => (servers || []).flatMap((server) => server.databases.filter((db) => db.key).map((db) => ({ ...db, server }))),
    [servers]
  );
  const keys = useMemo(() => entries.map((entry) => entry.key), [entries]);

  useEffect(() => {
    keys.forEach((key) => check(key));
  }, [keys, check]);

  useEffect(() => {
    if (!selected && keys.length) setSelected(keys[0]);
  }, [keys, selected]);

  const entry = entries.find((item) => item.key === selected);
  const labelFor = (name) => labels[name] || name;
  const current = health[selected];
  const fixes = (current?.checks || []).filter((c) => c.fix);
  const summary = useMemo(() => {
    const verdicts = keys.map((name) => health[name]?.verdict).filter(Boolean);
    return {
      ready: verdicts.filter((v) => v === "ready" || v === "readyWithWarnings").length,
      attention: verdicts.filter((v) => v === "needsAttention").length,
      other: verdicts.filter((v) => v === "empty" || v === "unreachable").length,
    };
  }, [keys, health]);

  // What the header chips are narrowing to, and whether anything survives it.
  const matchesVerdict = useCallback(
    (db) => !verdictFilter || (db.key && VERDICT_GROUP[health[db.key]?.verdict] === verdictFilter),
    [verdictFilter, health]
  );
  const visible = useMemo(
    () => (servers || []).flatMap((server) => server.databases).filter((db) => matchesTerm(db, query) && matchesVerdict(db)),
    [servers, query, matchesVerdict]
  );
  const filteredEmpty = Boolean(verdictFilter) && visible.length === 0;
  const toggleVerdict = (id) => {
    setVerdictFilter((current) => (current === id ? "" : id));
  };

  const describe = (db) => {
    const result = db.key ? health[db.key] : null;
    const names = db.workspaces.map((w) => w.name);
    return [
      db.key && db.key !== db.databaseName ? `${db.databaseName} on the server` : db.databaseName,
      result?.facts ? `${count(result.facts.objects)} objects` : null,
      db.key ? (names.length ? (names.length === 1 ? names[0] : `${names.length} workspaces`) : "no workspace") : "not connected",
    ]
      .filter(Boolean)
      .join(" · ");
  };

  const run = async (key, fn, fallback) => {
    setBusy(key);
    setError("");
    const res = await fn();
    setBusy("");
    if (res?.success) {
      setToast(res.msg || fallback);
      if (res.content) setHealth((all) => ({ ...all, [selected]: res.content }));
    } else setError(res?.msg || "That didn't work.");
  };

  const saveLabel = async () => {
    const value = labelDraft.trim();
    setBusy("label");
    const res = await setDatabaseLabel(selected, value);
    setBusy("");
    if (res?.success) {
      setLabels((all) => ({ ...all, [selected]: value }));
      setLabelDraft(null);
      setToast("Label saved. Users see it everywhere the database is offered.");
    } else setError(res?.msg || "Couldn't save the label.");
  };

  const archiveServer = async () => {
    const target = archiving;
    setBusy("archive");
    const res = await archiveDatabaseServer(target.id);
    setBusy("");
    setArchiving(null);
    if (res?.success) {
      setToast(res.msg || `${target.name} archived.`);
      loadServers();
    } else setError(res?.msg || "Couldn't archive the server.");
  };

  // The key when it has one, its name on the server when it has never been
  // connected - with the server, so the backend archives this database and no
  // other of the same name.
  const archiveDatabase = async () => {
    const target = archivingDatabase;
    setBusy("archive-database");
    setArchiveError("");
    const res = await archiveServerDatabase(target.key || target.databaseName, {
      detach,
      serverId: target.server.id,
      databaseName: target.databaseName,
    });
    setBusy("");
    if (res?.success) {
      setArchivingDatabase(null);
      setSelected("");
      setToast(res.msg || `${target.label || target.key || target.databaseName} archived.`);
      loadServers();
    } else setArchiveError(res?.msg || "Couldn't archive the database.");
  };

  // A refusal stays in the Archived dialog, beside the database it is about -
  // the reasons (its server is gone, its name was taken since) all end in
  // something to do here.
  const restoreDatabase = async (database) => {
    setBusy(`restore-${database.name}`);
    setRestoreError("");
    const res = await restoreServerDatabase(database.name);
    setBusy("");
    if (res?.success) {
      setToast(res.msg || `${database.label || database.name} restored. Add it to a workspace again to give people access.`);
      if (archived.length <= 1) setArchivedOpen(false);
      loadServers(database.name);
    } else setRestoreError(res?.msg || "Couldn't restore the database.");
  };

  const openArchive = (database, server) => {
    setDetach(false);
    setArchiveError("");
    setArchivingDatabase({ ...database, server: server || database.server });
  };

  const openConnect = (server, databaseName) => setDatabaseDialog({ open: true, mode: "connect", server, databaseName });
  const openCreate = (server) => setDatabaseDialog({ open: true, mode: "create", server, databaseName: null });

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, ...focusRing }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: brand.ink, color: brand.signal }}>
          <StorageOutlinedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Databases
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Every Neo4j server and its databases, and whether search, the explorer, diagrams and trial users will work on each.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          {[
            ["ready", `${summary.ready} ready`, tones.good, true],
            ["attention", `${summary.attention} need attention`, tones.fix, summary.attention > 0],
            ["other", `${summary.other} empty or offline`, tones.quiet, summary.other > 0],
          ].map(([id, label, tone, show]) =>
            show || verdictFilter === id ? (
              <CountChip key={id} id={id} label={label} tone={tone} active={verdictFilter === id} onToggle={toggleVerdict} />
            ) : null
          )}
          {/* Quiet until there is something to see, then the one way in to
              everything archived, on every server, cloud and on-premise. */}
          {archived.length > 0 && (
            <Button
              size="small"
              startIcon={<UnarchiveOutlinedIcon />}
              onClick={() => {
                setRestoreError("");
                setArchivedOpen(true);
              }}
            >
              Archived ({archived.length})
            </Button>
          )}
          {mayManageServers && (
            <Button variant="contained" size="small" startIcon={<AddRoundedIcon />} onClick={() => setServerDialog({ open: true, server: null })}>
              Connect a server
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Refusal
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
              <Button size="small" onClick={() => loadServers()}>
                Retry
              </Button>
              <IconButton aria-label="Dismiss" size="small" onClick={() => setError("")} sx={{ color: brand.muted }}>
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
        >
          {error}
        </Refusal>
      )}

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "400px minmax(0, 1fr)" }, alignItems: "start" }}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Find a database"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ bgcolor: "#fff", borderRadius: 2.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
          {servers && servers.length === 0 && !error && (
            <Surface>
              <EmptyState
                icon={<StorageOutlinedIcon />}
                title="No servers yet"
                action={
                  mayManageServers ? (
                    <Button variant="contained" size="small" startIcon={<AddRoundedIcon />} onClick={() => setServerDialog({ open: true, server: null })}>
                      Connect a server
                    </Button>
                  ) : null
                }
              >
                {mayManageServers ? "Connect your first server to see its databases here." : "Ask a platform admin to connect a server."}
              </EmptyState>
            </Surface>
          )}
          {filteredEmpty && (
            <Surface>
              <EmptyState
                icon={<StorageOutlinedIcon />}
                title="Nothing here right now"
                action={
                  <Button variant="contained" size="small" onClick={() => setVerdictFilter("")}>
                    Show all
                  </Button>
                }
              >
                No database matches that filter{query.trim() ? " and your search" : ""}.
              </EmptyState>
            </Surface>
          )}
          <ServerList
            servers={filteredEmpty ? [] : servers}
            query={query}
            filter={verdictFilter ? matchesVerdict : null}
            selected={selected}
            mayManage={mayManageServers}
            describe={describe}
            renderVerdict={(key) => <VerdictChip verdict={health[key]?.verdict} />}
            onSelect={(key) => {
              setSelected(key);
              setReviewing(null);
              setLabelDraft(null);
            }}
            onConnect={openConnect}
            onArchiveDatabase={openArchive}
            onCreate={openCreate}
            onEdit={(server) => setServerDialog({ open: true, server })}
            onArchive={(server) => setArchiving(server)}
          />
        </Stack>

        {servers && !entry && !error && (
          <Surface>
            <EmptyState icon={<StorageOutlinedIcon />} title="Choose a database">
              Pick one on the left to see whether it is ready. Databases on other servers appear once they are connected.
            </EmptyState>
          </Surface>
        )}

        {entry && (
          <Paper elevation={0} sx={{ borderRadius: 4, border: `1px solid ${brand.line}`, p: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {labelDraft === null ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>{labelFor(selected)}</Typography>
                    {mayLabel && (
                      <Tooltip title="Rename how users see it">
                        <IconButton size="small" onClick={() => setLabelDraft(labelFor(selected))} aria-label="Rename">
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField size="small" value={labelDraft} onChange={(e) => setLabelDraft(e.target.value)} autoFocus fullWidth={false} sx={{ width: 260 }} />
                    <Button variant="contained" size="small" disabled={!labelDraft.trim() || busy === "label"} onClick={saveLabel}>
                      Save
                    </Button>
                    <Button size="small" onClick={() => setLabelDraft(null)}>
                      Cancel
                    </Button>
                  </Stack>
                )}
                <Typography variant="body2" color="text.secondary">
                  {entry.databaseName} on {entry.server.name} · used by {entry.workspaces.map((w) => w.name).join(", ") || "no workspace yet"}
                </Typography>
              </Box>
              <VerdictChip verdict={current?.verdict} />
              <Tooltip title="Check again">
                <IconButton size="small" onClick={() => check(selected)} aria-label="Check again">
                  <RefreshRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {mayManageServers && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                {entry.server.status === "online" && (
                  <Button size="small" startIcon={<AddLinkRoundedIcon />} onClick={() => openConnect(entry.server, entry.databaseName)}>
                    Add to a workspace
                  </Button>
                )}
                {/* A connected database is archived whatever its server is
                    doing - only the registration is marked. One that was never
                    connected has to be found on its server first, so Archive
                    says why it can't be offered rather than failing. */}
                <Tooltip
                  title={
                    entry.registered || entry.server.status === "online"
                      ? ""
                      : `${entry.server.name} can't be reached, so this database can't be checked before it is archived. Try again when the server is back.`
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ArchiveOutlinedIcon />}
                      sx={quietDanger}
                      disabled={!entry.registered && entry.server.status !== "online"}
                      onClick={() => openArchive(entry)}
                    >
                      Archive
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            )}

            {current?.facts && (
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 1, mt: 2 }}>
                {[
                  ["Objects", current.facts.objects],
                  ["Types", current.facts.types],
                  ["Diagrams", current.facts.diagrams],
                  ["Trial samples", current.facts.inTrial],
                ].map(([label, value]) => (
                  <Box key={label} sx={{ p: 1.5, borderRadius: 3, bgcolor: brand.canvas, border: `1px solid ${brand.line}` }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.15rem" }}>{count(value)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {fixes.length > 0 && mayFix && (
              <Notice
                tone={tones.quiet}
                icon={<AutoFixHighOutlinedIcon fontSize="small" />}
                title={`${fixes.length === 1 ? "One thing" : `${fixes.length} things`} can be fixed from here`}
                sx={{ mt: 2, alignItems: "center" }}
                action={
                  <Button
                    variant="contained"
                    size="small"
                    disabled={Boolean(busy)}
                    startIcon={busy === "ready" ? <CircularProgress size={14} color="inherit" /> : null}
                    onClick={() => run("ready", () => makeDatabaseReady(selected), "Database prepared.")}
                    sx={{ flexShrink: 0 }}
                  >
                    Make ready
                  </Button>
                }
              >
                Fixes only add - nothing existing is changed or removed.
              </Notice>
            )}

            <Stack spacing={1} sx={{ mt: 2 }}>
              {!current ? (
                [0, 1, 2, 3].map((i) => <Skeleton key={i} height={56} />)
              ) : current.verdict === "unreachable" && !current.facts ? (
                <Stack alignItems="center" spacing={1} sx={{ py: 4, color: "text.secondary", textAlign: "center" }}>
                  <CloudOffOutlinedIcon />
                  <Typography sx={{ fontWeight: 650, color: "text.primary" }}>{current.checks[0]?.title}</Typography>
                  <Typography variant="body2">{current.checks[0]?.detail}</Typography>
                </Stack>
              ) : (
                current.checks.map((item) => (
                  <Box
                    key={item.key}
                    sx={{
                      borderRadius: 3,
                      border: `1px solid ${item.ok ? brand.hairline : item.severity === "warning" ? tones.wait.line : tones.fix.line}`,
                      bgcolor: item.ok ? "#fff" : item.severity === "warning" ? tones.wait.bg : tones.fix.bg,
                      overflow: "hidden",
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ p: 1.5 }}>
                      <Box sx={{ pt: 0.25 }}>
                        <CheckIcon check={item} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 650 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.detail}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                        {item.review && (
                          <Button
                            size="small"
                            onClick={() => setReviewing(reviewing === item.key ? null : item.key)}
                            endIcon={
                              <ExpandMoreRoundedIcon sx={{ transform: reviewing === item.key ? "rotate(180deg)" : "none", transition: "transform .15s ease" }} />
                            }
                          >
                            {reviewing === item.key ? "Hide" : "Review"}
                          </Button>
                        )}
                        {item.fix && mayFix && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={Boolean(busy)}
                            startIcon={busy === item.fix.action ? <CircularProgress size={14} color="inherit" /> : null}
                            onClick={() => run(item.fix.action, () => fixDatabase(selected, item.fix.action), "Fixed.")}
                          >
                            {item.fix.label}
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                    {item.review && (
                      <Collapse in={reviewing === item.key} unmountOnExit>
                        <AffectedList database={selected} check={item} />
                      </Collapse>
                    )}
                  </Box>
                ))
              )}
            </Stack>
          </Paper>
        )}
      </Box>

      <ServerDialog
        open={serverDialog.open}
        server={serverDialog.server}
        onClose={() => setServerDialog({ open: false, server: null })}
        onSaved={(msg) => {
          setServerDialog({ open: false, server: null });
          setToast(msg);
          loadServers();
        }}
      />

      <AddDatabaseDialog
        open={databaseDialog.open}
        mode={databaseDialog.mode}
        server={databaseDialog.server}
        databaseName={databaseDialog.databaseName}
        workspaces={workspaces}
        onClose={() => setDatabaseDialog((d) => ({ ...d, open: false }))}
        onDone={(msg, key) => {
          setDatabaseDialog((d) => ({ ...d, open: false }));
          setToast(msg);
          loadServers(key);
        }}
      />

      <Dialog open={Boolean(archiving)} onClose={busy === "archive" ? undefined : () => setArchiving(null)} maxWidth="xs" fullWidth PaperProps={{ className: "bfx", sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Archive {archiving?.name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Business Flows stops connecting to this server. Nothing on the server is changed or deleted, and it can be connected again later.
            A server whose databases a workspace still uses can't be archived.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setArchiving(null)} disabled={busy === "archive"}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={archiveServer}
            disabled={busy === "archive"}
            startIcon={busy === "archive" ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Archive server
          </Button>
        </DialogActions>
      </Dialog>

      <ArchivedDatabasesDialog
        open={archivedOpen}
        databases={archived}
        mayManage={mayManageServers}
        busy={busy}
        error={restoreError}
        onRestore={restoreDatabase}
        onClose={() => setArchivedOpen(false)}
      />

      <ArchiveDatabaseDialog
        database={archivingDatabase}
        busy={busy === "archive-database"}
        detach={detach}
        error={archiveError}
        onDetach={setDetach}
        onClose={() => setArchivingDatabase(null)}
        onConfirm={archiveDatabase}
      />

      <Toast notice={toast} onClose={() => setToast("")} />
    </Box>
  );
}
